/* eslint-disable immutable/no-mutation */
import { useCallback, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { getTournamentOptInMutation } from '../../graphql/TournamentOptInMutation.generated';
import useToastContext from '@/client/components/Toast/Context/useToastContext';
import sendInteractionToGtm from '@/client/helpers/sendToGtm/sendInteractionToGtm';
import sendEventToUmami from '@/client/helpers/trackEvent/sendEventToUmami';
import SessionStorage from '@/client/utils/storage/SessionStorage';

type Props = {
  tournamentId: string;
  page?: 'list' | 'details' | 'game';
  handle?: string;
  isLoggedIn: boolean;
  walletCurrency: string;
  updateTournament: () => Promise<void>;
  translations: Record<string, string>;
};

const useOptin = ({
  tournamentId,
  page,
  handle,
  isLoggedIn,
  walletCurrency,
  updateTournament,
  translations,
}: Props) => {
  const loading = useRef(false);
  const router = useRouter();
  const pathname = usePathname();
  const { showToast } = useToastContext();
  const redirectPath = pathname?.replace(/\?.*$/, '');
  const tournamentBannerId = SessionStorage.getItem<string>(
    'tournamentId',
  )?.replace(/"/g, '');
  const isGamePage = pathname?.includes('/play');
  const href = `/login?redirect=${redirectPath}`;

  const handleOptIn = useCallback(async () => {
    sendEventToUmami(
      {
        handle,
      },
      'optIn',
    );

    if (!isLoggedIn) {
      if (page === 'game' || isGamePage) {
        return window.dispatchEvent(new CustomEvent('showLoginPopover'));
      }
      return router.push(href);
    }

    if (tournamentBannerId === tournamentId) {
      sendInteractionToGtm({
        eventCategory: 'tournament_banner_optIn',
        eventAction: 'optIn_clicked',
        eventInfo: tournamentBannerId,
      });
    }
    loading.current = true;
    const { data } = await getTournamentOptInMutation({
      tournamentId,
      newValue: true,
      currency: walletCurrency,
    });
    loading.current = false;
    if (!data?.casinoTournamentsPublicSetOptInMutation?.errors) {
      return showToast({
        message: translations['tournaments.opt-in-successful'],
        variant: 'success',
        position: 'bottom',
        timeout: 5000,
      });
    }
    await updateTournament();
    return null;
  }, [
    translations,
    isLoggedIn,
    router,
    handle,
    tournamentBannerId,
    tournamentId,
    walletCurrency,
    page,
    isGamePage,
    href,
    showToast,
    updateTournament,
  ]);

  return { loading: loading.current, handleOptIn };
};

export default useOptin;
